<template>
  <div class="pm-page py-3">
    <header class="pm-toolbar">
      <div class="pm-toolbar__title">
        <span class="pm-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Payroll — Monthly Attendance</h1>
          <p>Per-employee insight for the month, built from the office biometric device. First punch = clock-in, last = clock-out. Use the pay-basis estimator to project payable amounts before running salary.</p>
        </div>
      </div>
      <div class="pm-toolbar__actions">
        <button type="button" class="pm-btn pm-btn--ghost" :disabled="isLoading || !rows.length" @click="exportCsv">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span>Export CSV</span>
        </button>
        <button type="button" class="pm-btn pm-btn--ghost" :class="{ 'is-loading': isLoading }" :disabled="isLoading" @click="loadReport">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="pm-btn__spin" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="pm-alert">{{ errorMessage }}</CAlert>

    <!-- ── Controls ── -->
    <section class="pm-section">
      <div class="pm-controls">
        <div class="pm-field pm-field--month">
          <label class="pm-label">Month</label>
          <div class="pm-month">
            <button type="button" class="pm-month__nav" aria-label="Previous month" @click="stepMonth(-1)">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <div class="pm-month__picker">
              <button type="button" class="pm-month__current" :class="{ 'is-open': monthPickerOpen }" @click="toggleMonthPicker">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                <span>{{ monthLabel(month) }}</span>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="pm-month__chev" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
              </button>
              <div v-if="monthPickerOpen" class="pm-monthpop">
                <div class="pm-monthpop__head">
                  <button type="button" class="pm-monthpop__nav" aria-label="Previous year" @click="popoverYear--">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                  </button>
                  <span class="pm-monthpop__year">{{ popoverYear }}</span>
                  <button type="button" class="pm-monthpop__nav" aria-label="Next year" :disabled="popoverYear >= currentYear" @click="popoverYear++">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                </div>
                <div class="pm-monthpop__grid">
                  <button
                    v-for="(mName, idx) in monthShortNames"
                    :key="mName"
                    type="button"
                    class="pm-monthpop__cell"
                    :class="{ 'is-active': isActiveMonthCell(idx), 'is-current': isCurrentMonthCell(idx) }"
                    :disabled="isFutureMonthCell(idx)"
                    @click="pickMonth(idx)"
                  >{{ mName }}</button>
                </div>
              </div>
            </div>
            <button type="button" class="pm-month__nav" aria-label="Next month" :disabled="!canGoNext" @click="stepMonth(1)">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
        </div>

        <div class="pm-field">
          <label class="pm-label">Device / Branch</label>
          <AppDropdown :value="deviceSn" :options="deviceOptions" @input="onDeviceChange" />
        </div>

        <div class="pm-field">
          <label class="pm-label">Show</label>
          <AppDropdown v-model="statusFilter" :options="statusOptions" />
        </div>

        <div class="pm-field pm-field--search">
          <label class="pm-label">Search</label>
          <div class="pm-search">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="pm-search__icon" aria-hidden="true"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input v-model.trim="search" type="text" class="pm-search__input" placeholder="Employee name or code…">
            <button v-if="search" type="button" class="pm-search__clear" aria-label="Clear search" @click="search = ''">×</button>
          </div>
        </div>

        <div class="pm-field pm-field--action">
          <label class="pm-label" aria-hidden="true">&nbsp;</label>
          <button type="button" class="pm-btn pm-btn--pay" :class="{ 'is-on': payEnabled }" @click="payEnabled = !payEnabled">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
            <span>{{ payEnabled ? 'Pay estimator on' : 'Pay estimator' }}</span>
          </button>
        </div>
      </div>

      <!-- pay-basis settings -->
      <transition name="pm-collapse">
        <div v-if="payEnabled" class="pm-paybar">
          <div class="pm-paybar__mode" role="tablist">
            <button
              v-for="m in payModes"
              :key="m.key"
              type="button"
              role="tab"
              class="pm-paychip"
              :class="{ 'is-active': payMode === m.key }"
              :aria-selected="payMode === m.key"
              @click="payMode = m.key"
            >{{ m.label }}</button>
          </div>
          <div class="pm-paybar__rate">
            <label class="pm-paybar__rlabel">{{ activePayMode.rateLabel }}</label>
            <div class="pm-rateinput">
              <span class="pm-rateinput__cur">₹</span>
              <input v-model.number="payDefaults[payMode]" type="number" min="0" step="1" class="pm-rateinput__field" :placeholder="activePayMode.placeholder">
            </div>
          </div>
          <div class="pm-paybar__rate">
            <label class="pm-paybar__rlabel">Overtime rate / hour</label>
            <div class="pm-rateinput">
              <span class="pm-rateinput__cur">₹</span>
              <input v-model.number="payOvertimeRate" type="number" min="0" step="1" class="pm-rateinput__field" placeholder="e.g. 120">
            </div>
          </div>
          <div class="pm-paybar__rate">
            <label class="pm-paybar__rlabel">Std hours / day <small class="pm-paybar__hint">OT after</small></label>
            <div class="pm-rateinput pm-rateinput--sm">
              <input v-model.number="standardHours" type="number" min="1" max="24" step="0.5" class="pm-rateinput__field">
              <span class="pm-rateinput__suf">h</span>
            </div>
          </div>
          <div class="pm-paybar__rate">
            <label class="pm-paybar__rlabel">OT grace <small class="pm-paybar__hint">ignore minor overage</small></label>
            <div class="pm-rateinput pm-rateinput--sm">
              <input v-model.number="payOvertimeGrace" type="number" min="0" max="120" step="5" class="pm-rateinput__field">
              <span class="pm-rateinput__suf">min</span>
            </div>
          </div>
          <p class="pm-paybar__note">{{ activePayMode.note }} A day counts as overtime only once it exceeds {{ standardHours }}h by more than the {{ payOvertimeGrace || 0 }}-min grace (so 8h 01m is not OT). Per-employee rate overrides can be set from each profile. Estimates only — not a payslip.</p>
        </div>
      </transition>
    </section>

    <CAlert v-if="unmappedCount" color="warning" show class="pm-alert">
      <strong>{{ unmappedCount }}</strong> unmapped {{ unmappedCount === 1 ? 'PIN is' : 'PINs are' }} punching but not linked to a staff member — map them so their hours and pay are attributed.
    </CAlert>

    <!-- ── KPI hero ── -->
    <div class="pm-kpis">
      <div v-for="kpi in kpis" :key="kpi.key" class="pm-kpi" :class="{ 'pm-kpi--accent': kpi.accent }">
        <span class="pm-kpi__icon" :class="'pm-kpi__icon--' + kpi.tone" aria-hidden="true" v-html="kpi.icon"></span>
        <div class="pm-kpi__meta">
          <span class="pm-kpi__label">{{ kpi.label }}</span>
          <span class="pm-kpi__value">{{ kpi.value }}<small v-if="kpi.unit" class="pm-kpi__unit">{{ kpi.unit }}</small></span>
          <span v-if="kpi.sub" class="pm-kpi__sub">{{ kpi.sub }}</span>
        </div>
      </div>
    </div>

    <!-- ── Office rhythm ── -->
    <section v-if="!isLoading && officeDaily.length" class="pm-section pm-rhythm">
      <header class="pm-rhythm__head">
        <div>
          <h2>Daily attendance rhythm</h2>
          <small>Headcount on-site each day across {{ monthLabel(month) }}</small>
        </div>
        <div class="pm-rhythm__legend">
          <span class="pm-rhythm__peak">Peak {{ peakHeadcount }}</span>
          <span class="pm-rhythm__total">{{ formatHours(totalHours) }}h total</span>
        </div>
      </header>
      <div class="pm-rhythm__chart" role="img" :aria-label="'Daily headcount for ' + monthLabel(month)">
        <div
          v-for="d in officeDaily"
          :key="d.date"
          class="pm-rbar"
          :class="{ 'is-weekend': d.weekend, 'is-empty': !d.present }"
          :title="d.tooltip"
        >
          <span class="pm-rbar__fill" :style="{ height: d.heightPct + '%' }"></span>
          <span class="pm-rbar__day">{{ d.day }}</span>
        </div>
      </div>
    </section>

    <!-- ── Employee table ── -->
    <BaseTable
      title="Employees"
      :items="filteredRows"
      :columns="columns"
      :loading="isLoading"
      :paginated="filteredRows.length > pageSize"
      :page-size="pageSize"
      :default-sort="{ column: 'attendancePct', direction: 'desc' }"
      empty-text="No attendance recorded for this month."
      @row-click="openEmployee"
    >
      <template #toolbar>
        <span v-if="!isLoading && rows.length" class="pm-hint">{{ filteredRows.length }} of {{ rows.length }} shown</span>
      </template>

      <template #cell-name="{ row }">
        <div class="pm-emp">
          <span class="pm-avatar" :class="{ 'pm-avatar--unmapped': row.is_unmapped, 'pm-avatar--photo': photoUrl(row) }">
            <img v-if="photoUrl(row)" :src="photoUrl(row)" :alt="row.name" class="pm-avatar__img" @error="onAvatarError(row)">
            <span v-else>{{ row.initials }}</span>
          </span>
          <div class="pm-emp__text">
            <span class="pm-emp__name">{{ row.name }}</span>
            <span v-if="row.employee_code" class="pm-emp__code">{{ row.employee_code }}</span>
            <span v-else-if="row.is_unmapped" class="pm-emp__code pm-emp__code--warn">Needs mapping</span>
          </div>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="pm-emp__open" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
        </div>
      </template>

      <template #cell-attendancePct="{ row }">
        <div class="pm-ring" :title="row.present_days + ' of ' + officeActiveDays + ' working days'">
          <svg viewBox="0 0 36 36" class="pm-ring__svg">
            <circle class="pm-ring__track" cx="18" cy="18" r="15.5" />
            <circle class="pm-ring__fill" :class="'pm-ring__fill--' + ringTone(row.attendancePct)" cx="18" cy="18" r="15.5"
              :stroke-dasharray="ringDash(row.attendancePct)" />
          </svg>
          <span class="pm-ring__pct">{{ row.attendancePct }}<small>%</small></span>
        </div>
      </template>

      <template #cell-present_days="{ row }">
        <div class="pm-days">
          <span class="pm-days__main">{{ row.present_days }}<span class="pm-days__sep">/</span><span class="pm-days__den">{{ officeActiveDays }}</span></span>
          <div class="pm-days__chips">
            <span v-if="row.complete_days" class="pm-chip pm-chip--green" title="Full days (in + out)">{{ row.complete_days }}</span>
            <span v-if="row.incomplete_days" class="pm-chip pm-chip--amber" title="One-punch days">{{ row.incomplete_days }}</span>
            <span v-if="row.absent_days" class="pm-chip pm-chip--red" title="Absent (no punch on a working day)">{{ row.absent_days }}</span>
          </div>
        </div>
      </template>

      <template #cell-total_hours="{ row }">
        <div class="pm-tot">
          <span class="pm-num">{{ formatHours(row.total_hours) }}<small class="pm-num__u">h</small></span>
          <span v-if="row.overtime_hours > 0" class="pm-tot__reg">{{ formatHours(row.regular_hours) }}h reg</span>
        </div>
      </template>

      <template #cell-overtime_hours="{ row }">
        <span v-if="row.overtime_hours > 0" class="pm-ot" :title="row.ot_days + (row.ot_days === 1 ? ' day' : ' days') + ' with overtime'">
          +{{ formatHours(row.overtime_hours) }}<small class="pm-ot__u">h</small>
        </span>
        <span v-else class="pm-muted">—</span>
      </template>

      <template #cell-avg_hours="{ row }">
        <span v-if="row.present_days" class="pm-num pm-num--muted">{{ formatHours(row.avg_hours) }}<small class="pm-num__u">h</small></span>
        <span v-else class="pm-muted">—</span>
      </template>

      <template #cell-rhythm="{ row }">
        <div class="pm-spark" :aria-label="'Daily hours for ' + row.name">
          <span
            v-for="cell in row.sparkline"
            :key="cell.day"
            class="pm-spark__bar"
            :class="'pm-spark__bar--' + cell.tone"
            :style="{ height: cell.heightPct + '%' }"
            :title="cell.title"
          ></span>
        </div>
      </template>

      <template #cell-estimated_pay="{ row }">
        <span v-if="row.estimated_pay != null" class="pm-pay">{{ formatMoney(row.estimated_pay) }}</span>
        <span v-else class="pm-muted">—</span>
      </template>
    </BaseTable>

    <!-- ── Employee drawer ── -->
    <transition name="pm-drawer">
      <div v-if="selected" class="pm-drawer" @keydown.esc="closeEmployee">
        <div class="pm-drawer__scrim" @click="closeEmployee"></div>
        <aside class="pm-drawer__panel" role="dialog" aria-modal="true" :aria-label="'Attendance detail for ' + selected.name">
          <header class="pm-drawer__head">
            <div class="pm-drawer__id">
              <span class="pm-avatar pm-avatar--lg" :class="{ 'pm-avatar--unmapped': selected.is_unmapped, 'pm-avatar--photo': photoUrl(selected) }">
                <img v-if="photoUrl(selected)" :src="photoUrl(selected)" :alt="selected.name" class="pm-avatar__img" @error="onAvatarError(selected)">
                <span v-else>{{ selected.initials }}</span>
              </span>
              <div>
                <h3>{{ selected.name }}</h3>
                <p>{{ selected.employee_code || (selected.is_unmapped ? 'Unmapped PIN' : '—') }} · {{ monthLabel(month) }}</p>
              </div>
            </div>
            <button type="button" class="pm-drawer__close" aria-label="Close" @click="closeEmployee">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </header>

          <div class="pm-drawer__body">
            <!-- stat grid -->
            <div class="pm-statgrid">
              <div class="pm-stat">
                <span class="pm-stat__label">Attendance</span>
                <span class="pm-stat__value">{{ selected.attendancePct }}<small>%</small></span>
              </div>
              <div class="pm-stat">
                <span class="pm-stat__label">Present Days</span>
                <span class="pm-stat__value">{{ selected.present_days }}<small>/ {{ officeActiveDays }}</small></span>
              </div>
              <div class="pm-stat">
                <span class="pm-stat__label">Total Hours</span>
                <span class="pm-stat__value">{{ formatHours(selected.total_hours) }}<small>h</small></span>
              </div>
              <div class="pm-stat">
                <span class="pm-stat__label">Regular Hours</span>
                <span class="pm-stat__value">{{ formatHours(selected.regular_hours) }}<small>h</small></span>
              </div>
              <div class="pm-stat pm-stat--ot">
                <span class="pm-stat__label">Overtime</span>
                <span class="pm-stat__value">{{ formatHours(selected.overtime_hours) }}<small>h</small></span>
                <span v-if="selected.ot_days" class="pm-stat__foot">over {{ selected.ot_days }} {{ selected.ot_days === 1 ? 'day' : 'days' }}</span>
              </div>
              <div class="pm-stat">
                <span class="pm-stat__label">Avg / Day</span>
                <span class="pm-stat__value">{{ formatHours(selected.avg_hours) }}<small>h</small></span>
              </div>
              <div class="pm-stat">
                <span class="pm-stat__label">One-punch Days</span>
                <span class="pm-stat__value">{{ selected.incomplete_days }}</span>
              </div>
              <div class="pm-stat">
                <span class="pm-stat__label">Busiest Day</span>
                <span class="pm-stat__value pm-stat__value--sm">{{ selected.busiest_day ? dayLabel(selected.busiest_day) : '—' }}</span>
              </div>
            </div>

            <!-- calendar heatmap -->
            <div class="pm-cal">
              <div class="pm-cal__head">
                <h4>Daily heatmap</h4>
                <div class="pm-cal__legend">
                  <span class="pm-cal__lg">Less</span>
                  <span class="pm-cal__swatch" style="background:#e6f5ef"></span>
                  <span class="pm-cal__swatch" style="background:#9fdcc4"></span>
                  <span class="pm-cal__swatch" style="background:#3bb389"></span>
                  <span class="pm-cal__swatch" style="background:#0e5b46"></span>
                  <span class="pm-cal__lg">More</span>
                </div>
              </div>
              <div class="pm-cal__dow">
                <span v-for="d in ['S','M','T','W','T','F','S']" :key="d">{{ d }}</span>
              </div>
              <div class="pm-cal__grid">
                <div
                  v-for="(cell, i) in calendarCells"
                  :key="i"
                  class="pm-cal__cell"
                  :class="{ 'is-blank': !cell, 'is-incomplete': cell && cell.info && cell.info.status === 'incomplete', 'is-absent': cell && cell.absent, 'is-empty': cell && !cell.info && !cell.absent }"
                  :style="cell && cell.info ? heatStyle(cell.info) : null"
                  :title="cell ? cellTooltip(cell) : ''"
                >
                  <span v-if="cell" class="pm-cal__num">{{ cell.day }}</span>
                  <span v-if="cell && cell.info && cell.info.overtime > 0" class="pm-cal__ot" aria-label="overtime">OT</span>
                </div>
              </div>
            </div>

            <!-- pay estimator -->
            <div v-if="payEnabled" class="pm-paycard">
              <div class="pm-paycard__head">
                <h4>Pay estimate</h4>
                <span class="pm-paycard__mode">{{ activePayMode.label }}</span>
              </div>
              <div class="pm-paycard__row">
                <div class="pm-paycard__field">
                  <label class="pm-label">{{ activePayMode.rateLabel }} <small v-if="!hasOverride(selected)" class="pm-paycard__def">(default)</small></label>
                  <div class="pm-rateinput">
                    <span class="pm-rateinput__cur">₹</span>
                    <input
                      type="number" min="0" step="1" class="pm-rateinput__field"
                      :value="employeeRate(selected)"
                      :placeholder="String(payDefaults[payMode] || '')"
                      @input="setRate(selected, $event.target.value)"
                    >
                    <button v-if="hasOverride(selected)" type="button" class="pm-rateinput__reset" title="Reset to default" @click="clearRate(selected)">↺</button>
                  </div>
                </div>
                <div class="pm-paycard__result">
                  <span class="pm-paycard__rlabel">Estimated payable</span>
                  <span class="pm-paycard__amount">{{ selected.estimated_pay != null ? formatMoney(selected.estimated_pay) : '—' }}</span>
                </div>
              </div>
            </div>

            <!-- per-day table -->
            <div class="pm-daylist">
              <h4>Day-by-day <small v-if="selectedAbsentCount" class="pm-daylist__count">{{ selectedAbsentCount }} absent</small></h4>
              <div v-if="!selectedDayRows.length" class="pm-daylist__empty">No working days recorded this month.</div>
              <table v-else class="pm-daytable">
                <thead>
                  <tr><th>Date</th><th>In</th><th>Out</th><th class="pm-r">Hours</th><th class="pm-r">OT</th><th>Status</th></tr>
                </thead>
                <tbody>
                  <tr v-for="d in selectedDayRows" :key="d.date" :class="{ 'is-absent': d.status === 'absent' }">
                    <td>{{ dayLabel(d.date) }}</td>
                    <td class="pm-mono">{{ timeDisplay(d.first_in) }}</td>
                    <td class="pm-mono">{{ timeDisplay(d.last_out) }}</td>
                    <td class="pm-r pm-mono">
                      <span v-if="d.status === 'absent'" class="pm-muted">—</span>
                      <span v-else>{{ formatHours(d.hours) }}h</span>
                    </td>
                    <td class="pm-r pm-mono">
                      <span v-if="d.overtime > 0" class="pm-ot-tag">+{{ formatHours(d.overtime) }}h</span>
                      <span v-else class="pm-muted">—</span>
                    </td>
                    <td><span :class="['pm-pill', 'pm-pill--' + d.status]"><span class="pm-pill__dot"></span>{{ statusLabel(d.status) }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </aside>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import BaseTable from '@/components/BaseTable.vue'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const STORAGE_KEY = 'payroll.attendance.settings'

export default {
  name: 'PayrollMonthlyAttendance',
  components: { AppDropdown, BaseTable },
  data () {
    const now = new Date()
    return {
      month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
      rows: [],
      meta: { days_in_month: 30, office_active_days: 0, active_dates: [] },
      isLoading: false,
      errorMessage: '',
      search: '',
      statusFilter: '',
      deviceSn: (this.$route && this.$route.query && this.$route.query.device) || '',
      devices: [],
      pageSize: 25,
      brokenAvatars: {},
      selectedKey: null,
      monthPickerOpen: false,
      popoverYear: now.getFullYear(),
      monthShortNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      // pay estimator
      payEnabled: false,
      payMode: 'monthly',
      standardHours: 8,
      payOvertimeRate: null,
      payOvertimeGrace: 15,
      payDefaults: { monthly: null, daily: null, hourly: null },
      payRates: {},
      payModes: [
        { key: 'monthly', label: 'Monthly base', rateLabel: 'Monthly base salary', placeholder: 'e.g. 18000', note: 'Monthly base prorated by attendance (present ÷ working days), plus OT rate × overtime hours.' },
        { key: 'daily', label: 'Per day', rateLabel: 'Rate per present day', placeholder: 'e.g. 700', note: 'Daily rate × present days, plus OT rate × overtime hours.' },
        { key: 'hourly', label: 'Per hour', rateLabel: 'Rate per regular hour', placeholder: 'e.g. 90', note: 'Hourly rate × regular hours (capped at the daily standard), plus OT rate × overtime hours.' }
      ],
      statusOptions: [
        { value: '', label: 'All staff' },
        { value: 'active', label: 'Worked this month' },
        { value: 'absent', label: 'Absent all month' },
        { value: 'incomplete', label: 'Has one-punch days' },
        { value: 'unmapped', label: 'Unmapped PINs' }
      ]
    }
  },
  computed: {
    currentYear () {
      return new Date().getFullYear()
    },
    currentMonth () {
      const n = new Date()
      return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`
    },
    canGoNext () {
      return this.month < this.currentMonth
    },
    activePayMode () {
      return this.payModes.find(m => m.key === this.payMode) || this.payModes[0]
    },
    officeActiveDays () {
      return this.meta.office_active_days || 0
    },
    deviceOptions () {
      return [{ value: '', label: 'All Devices' }].concat(
        this.devices.map(d => ({
          value: d.serial,
          label: [d.branch_name, d.label].filter(Boolean).join(' · ') || d.serial
        }))
      )
    },
    columns () {
      const base = [
        { key: 'name', label: 'Employee' },
        { key: 'attendancePct', label: 'Attendance', align: 'center', width: '110px' },
        { key: 'present_days', label: 'Days', align: 'center', width: '150px' },
        { key: 'total_hours', label: 'Total Hours', align: 'right', width: '120px' },
        { key: 'overtime_hours', label: 'Overtime', align: 'right', width: '105px' },
        { key: 'avg_hours', label: 'Avg / Day', align: 'right', width: '100px' },
        { key: 'rhythm', label: 'Monthly Rhythm', sortable: false, width: '170px' }
      ]
      if (this.payEnabled) base.push({ key: 'estimated_pay', label: 'Est. Pay', align: 'right', width: '130px' })
      return base
    },
    decoratedRows () {
      const days = this.meta.days_in_month || 30
      const active = this.officeActiveDays || 0
      const std = Number(this.standardHours) || 8
      // Grace buffer: a few minutes over the standard shouldn't read as OT.
      // OT is only credited once the day exceeds the standard PLUS this grace.
      const graceH = Math.max(0, Number(this.payOvertimeGrace) || 0) / 60
      // Give the sparkline headroom so an overtime day visibly exceeds a
      // standard day rather than both pinning at full height.
      const barMax = Math.max(std * 1.5, 1)
      return this.rows.map((r) => {
        const dayMap = {}
        let regular = 0
        let overtime = 0
        let otDays = 0
        // Daily overtime rule: anything over the standard threshold IN A DAY is
        // OT. Split each day, then sum, so a 10h day = 8h regular + 2h OT.
        const decoratedDays = r.days.map((d) => {
          const over = Math.max(0, d.hours - std)
          // Within the grace window the extra minutes are ignored (not OT, not
          // extra regular). Once the day crosses std + grace, the whole excess
          // beyond the standard is credited as OT.
          const ot = over > graceH ? over : 0
          const reg = Math.min(d.hours, std)
          regular += reg
          overtime += ot
          if (ot > 0) otDays += 1
          const dd = { ...d, regular: Math.round(reg * 100) / 100, overtime: Math.round(ot * 100) / 100 }
          dayMap[d.date] = dd
          return dd
        })
        const sparkline = []
        for (let dn = 1; dn <= days; dn++) {
          const iso = `${this.month}-${String(dn).padStart(2, '0')}`
          const info = dayMap[iso]
          const hours = info ? info.hours : 0
          let tone = 'none'
          if (info) tone = info.status === 'incomplete' ? 'amber' : (info.overtime > 0 ? 'ot' : 'green')
          sparkline.push({
            day: dn,
            tone,
            heightPct: info ? Math.max(14, Math.min(100, Math.round((hours / barMax) * 100))) : 6,
            title: info
              ? `${this.dayLabel(iso)} · ${this.formatHours(hours)}h${info.overtime > 0 ? ' (' + this.formatHours(info.overtime) + 'h OT)' : ''} · ${info.punches} punches`
              : `${this.dayLabel(iso)} · no punch`
          })
        }
        const attendancePct = active ? Math.round((r.present_days / active) * 100) : 0
        const absentDays = Math.max(0, active - r.present_days)
        const decorated = {
          ...r,
          days: decoratedDays,
          dayMap,
          sparkline,
          attendancePct,
          absent_days: absentDays,
          regular_hours: Math.round(regular * 100) / 100,
          overtime_hours: Math.round(overtime * 100) / 100,
          ot_days: otDays,
          initials: this.initials(r.name, r.is_unmapped),
          key: r.user_id != null ? `u${r.user_id}` : `n${r.name}`
        }
        decorated.estimated_pay = this.payEnabled ? this.computePay(decorated) : null
        return decorated
      })
    },
    filteredRows () {
      const q = this.search.toLowerCase()
      return this.decoratedRows.filter((r) => {
        if (this.statusFilter === 'active' && r.present_days === 0) return false
        if (this.statusFilter === 'absent' && r.present_days > 0) return false
        if (this.statusFilter === 'incomplete' && !r.incomplete_days) return false
        if (this.statusFilter === 'unmapped' && !r.is_unmapped) return false
        if (!q) return true
        return (r.name || '').toLowerCase().includes(q) || (r.employee_code || '').toLowerCase().includes(q)
      })
    },
    realStaff () {
      return this.decoratedRows.filter(r => !r.is_unmapped)
    },
    unmappedCount () {
      return this.decoratedRows.filter(r => r.is_unmapped).length
    },
    activeStaff () {
      return this.realStaff.filter(r => r.present_days > 0).length
    },
    totalHours () {
      return this.realStaff.reduce((s, r) => s + (Number(r.total_hours) || 0), 0)
    },
    totalOvertime () {
      return this.realStaff.reduce((s, r) => s + (Number(r.overtime_hours) || 0), 0)
    },
    totalPresentDays () {
      return this.realStaff.reduce((s, r) => s + (Number(r.present_days) || 0), 0)
    },
    avgAttendance () {
      const denom = this.realStaff.length * this.officeActiveDays
      return denom ? Math.round((this.totalPresentDays / denom) * 100) : 0
    },
    totalPayroll () {
      return this.realStaff.reduce((s, r) => s + (Number(r.estimated_pay) || 0), 0)
    },
    peakHeadcount () {
      return this.officeDaily.reduce((m, d) => Math.max(m, d.present), 0)
    },
    officeDaily () {
      if (!this.rows.length) return []
      const days = this.meta.days_in_month || 30
      const [y, m] = this.month.split('-').map(Number)
      const perDay = {}
      this.realStaff.forEach((r) => {
        r.days.forEach((d) => {
          if (!perDay[d.date]) perDay[d.date] = { present: 0, hours: 0 }
          perDay[d.date].present += 1
          perDay[d.date].hours += Number(d.hours) || 0
        })
      })
      const peak = Math.max(1, ...Object.values(perDay).map(v => v.present))
      const out = []
      for (let dn = 1; dn <= days; dn++) {
        const iso = `${this.month}-${String(dn).padStart(2, '0')}`
        const v = perDay[iso] || { present: 0, hours: 0 }
        const wd = new Date(y, m - 1, dn).getDay()
        out.push({
          date: iso,
          day: dn,
          present: v.present,
          weekend: wd === 0,
          heightPct: v.present ? Math.max(8, Math.round((v.present / peak) * 100)) : 0,
          tooltip: `${this.dayLabel(iso)} · ${v.present} present · ${this.formatHours(v.hours)}h`
        })
      }
      return out
    },
    selected () {
      if (!this.selectedKey) return null
      return this.decoratedRows.find(r => r.key === this.selectedKey) || null
    },
    selectedAbsentCount () {
      return this.selected ? this.selected.absent_days : 0
    },
    activeDateSet () {
      return new Set(this.meta.active_dates || [])
    },
    calendarCells () {
      if (!this.selected) return []
      const [y, m] = this.month.split('-').map(Number)
      const daysInMonth = new Date(y, m, 0).getDate()
      const startWeekday = new Date(y, m - 1, 1).getDay()
      const cells = []
      for (let i = 0; i < startWeekday; i++) cells.push(null)
      for (let dn = 1; dn <= daysInMonth; dn++) {
        const iso = `${this.month}-${String(dn).padStart(2, '0')}`
        const info = this.selected.dayMap[iso] || null
        // A working day the office was open but this person didn't punch =
        // absent. Days the office was closed stay neutral, not red.
        cells.push({ day: dn, date: iso, info, absent: !info && this.activeDateSet.has(iso) })
      }
      while (cells.length % 7) cells.push(null)
      return cells
    },
    // Every office working day for the selected employee, so absences are listed
    // alongside the days they were present.
    selectedDayRows () {
      if (!this.selected) return []
      const map = this.selected.dayMap
      const dates = new Set(this.meta.active_dates || [])
      Object.keys(map).forEach(d => dates.add(d))
      return Array.from(dates).sort().map((date) => {
        if (map[date]) return map[date]
        return { date, first_in: null, last_out: null, hours: 0, overtime: 0, punches: 0, status: 'absent' }
      })
    },
    kpis () {
      const standardHoursLabel = this.formatHours(this.standardHours)
      const out = [
        {
          key: 'staff',
          label: 'Active Staff',
          value: this.activeStaff,
          sub: `of ${this.realStaff.length} mapped`,
          tone: 'brand',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>'
        },
        {
          key: 'days',
          label: 'Working Days',
          value: this.officeActiveDays,
          sub: `${this.meta.days_in_month || 0} calendar days`,
          tone: 'days',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>'
        },
        {
          key: 'present',
          label: 'Present Days',
          value: this.totalPresentDays,
          sub: 'across all staff',
          tone: 'present',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>'
        },
        {
          key: 'overtime',
          label: 'Overtime Hours',
          value: this.formatHours(this.totalOvertime),
          unit: 'h',
          sub: 'beyond ' + standardHoursLabel + 'h / day',
          tone: 'ot',
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>'
        }
      ]
      if (this.payEnabled) {
        out.push({
          key: 'payroll',
          label: 'Est. Payroll',
          value: this.formatMoney(this.totalPayroll),
          tone: 'brand',
          accent: true,
          icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>'
        })
      }
      return out
    }
  },
  watch: {
    payEnabled () { this.persistSettings() },
    payMode () { this.persistSettings() },
    standardHours () { this.persistSettings() },
    payOvertimeRate () { this.persistSettings() },
    payOvertimeGrace () { this.persistSettings() },
    payDefaults: { deep: true, handler () { this.persistSettings() } },
    payRates: { deep: true, handler () { this.persistSettings() } }
  },
  created () {
    this.restoreSettings()
    this.loadDevices()
    this.loadReport()
  },
  mounted () {
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    monthLabel (ym) {
      if (!ym) return ''
      const [y, m] = ym.split('-').map(Number)
      return `${MONTH_NAMES[m - 1]} ${y}`
    },
    dayLabel (iso) {
      if (!iso) return '—'
      const [y, m, d] = iso.split('-').map(Number)
      const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(y, m - 1, d).getDay()]
      return `${wd} ${String(d).padStart(2, '0')} ${MONTH_NAMES[m - 1].slice(0, 3)}`
    },
    stepMonth (dir) {
      const [y, m] = this.month.split('-').map(Number)
      const dt = new Date(y, m - 1 + dir, 1)
      const next = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
      if (dir > 0 && next > this.currentMonth) return
      this.month = next
      this.popoverYear = dt.getFullYear()
      this.loadReport()
    },
    toggleMonthPicker () {
      this.monthPickerOpen = !this.monthPickerOpen
      if (this.monthPickerOpen) this.popoverYear = Number(this.month.split('-')[0])
    },
    pickMonth (idx) {
      const next = `${this.popoverYear}-${String(idx + 1).padStart(2, '0')}`
      if (next > this.currentMonth) return
      this.month = next
      this.monthPickerOpen = false
      this.loadReport()
    },
    isActiveMonthCell (idx) {
      return this.month === `${this.popoverYear}-${String(idx + 1).padStart(2, '0')}`
    },
    isCurrentMonthCell (idx) {
      return this.currentMonth === `${this.popoverYear}-${String(idx + 1).padStart(2, '0')}`
    },
    isFutureMonthCell (idx) {
      return `${this.popoverYear}-${String(idx + 1).padStart(2, '0')}` > this.currentMonth
    },
    onDocClick (e) {
      if (this.monthPickerOpen && !e.target.closest('.pm-month__picker')) this.monthPickerOpen = false
    },
    initials (name, isUnmapped) {
      if (isUnmapped) return '?'
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '–'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },
    avatarKey (row) {
      return row.user_id != null ? `u${row.user_id}` : `n${row.name}`
    },
    photoUrl (row) {
      if (!row || !row.image || typeof row.image !== 'string') return ''
      if (this.brokenAvatars[this.avatarKey(row)]) return ''
      return row.image
    },
    onAvatarError (row) {
      this.$set(this.brokenAvatars, this.avatarKey(row), true)
    },
    timeDisplay (t) {
      if (!t) return '—'
      const [hStr, mStr] = String(t).split(':')
      let h = Number(hStr)
      const m = mStr || '00'
      if (!Number.isFinite(h)) return t
      const suffix = h >= 12 ? 'PM' : 'AM'
      h = h % 12 || 12
      return `${h}:${m} ${suffix}`
    },
    formatHours (hours) {
      const n = Number(hours) || 0
      return (Math.round(n * 10) / 10).toFixed(1)
    },
    formatMoney (value) {
      const n = Number(value) || 0
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
    },
    statusLabel (s) {
      return { present: 'Present', absent: 'Absent', incomplete: 'One Punch' }[s] || s
    },
    ringTone (pct) {
      if (pct >= 80) return 'full'
      if (pct >= 50) return 'mid'
      return 'low'
    },
    ringDash (pct) {
      const c = 2 * Math.PI * 15.5
      const filled = (Math.max(0, Math.min(100, pct)) / 100) * c
      return `${filled} ${c - filled}`
    },
    heatStyle (info) {
      if (!info) return {}
      if (info.status === 'incomplete') return { background: '#fef3c7', color: '#92400e' }
      const std = Number(this.standardHours) || 9
      const intensity = Math.max(0, Math.min(1, info.hours / std))
      const alpha = 0.16 + intensity * 0.82
      return {
        background: `rgba(14, 91, 70, ${alpha.toFixed(3)})`,
        color: intensity > 0.45 ? '#ffffff' : '#0e5b46'
      }
    },
    cellTooltip (cell) {
      if (!cell.info) return `${this.dayLabel(cell.date)} · ${cell.absent ? 'absent' : 'no punch'}`
      const ot = cell.info.overtime > 0 ? ` · ${this.formatHours(cell.info.overtime)}h OT` : ''
      return `${this.dayLabel(cell.date)} · ${this.formatHours(cell.info.hours)}h${ot} · in ${this.timeDisplay(cell.info.first_in)} · out ${this.timeDisplay(cell.info.last_out)}`
    },
    // ── pay estimator ──
    employeeRate (row) {
      const over = this.payRates[row.key] && this.payRates[row.key][this.payMode]
      if (over != null && over !== '') return over
      return this.payDefaults[this.payMode] != null ? this.payDefaults[this.payMode] : ''
    },
    hasOverride (row) {
      const r = this.payRates[row.key]
      return !!(r && r[this.payMode] != null && r[this.payMode] !== '')
    },
    setRate (row, value) {
      const num = value === '' ? null : Number(value)
      const existing = this.payRates[row.key] || {}
      this.$set(this.payRates, row.key, { ...existing, [this.payMode]: num })
    },
    clearRate (row) {
      const existing = { ...(this.payRates[row.key] || {}) }
      delete existing[this.payMode]
      this.$set(this.payRates, row.key, existing)
    },
    computePay (row) {
      if (row.is_unmapped) return null
      const override = this.payRates[row.key] && this.payRates[row.key][this.payMode]
      const rate = Number(override != null && override !== '' ? override : this.payDefaults[this.payMode]) || 0
      const otRate = Number(this.payOvertimeRate) || 0
      const otPay = otRate * (row.overtime_hours || 0)
      if (!rate && !otPay) return null
      let base = 0
      if (this.payMode === 'hourly') {
        // Regular hours at base rate; OT hours are paid only via the OT rate so
        // they aren't counted twice.
        base = rate * (row.regular_hours || 0)
      } else if (this.payMode === 'daily') {
        base = rate * row.present_days
      } else {
        // Monthly base prorated by attendance.
        const denom = this.officeActiveDays || 0
        base = denom ? rate * (row.present_days / denom) : rate
      }
      return Math.round(base + otPay)
    },
    persistSettings () {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          payEnabled: this.payEnabled,
          payMode: this.payMode,
          standardHours: this.standardHours,
          payOvertimeRate: this.payOvertimeRate,
          payOvertimeGrace: this.payOvertimeGrace,
          payDefaults: this.payDefaults,
          payRates: this.payRates
        }))
      } catch (e) { /* storage unavailable — non-fatal */ }
    },
    restoreSettings () {
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const s = JSON.parse(raw)
        if (typeof s.payEnabled === 'boolean') this.payEnabled = s.payEnabled
        if (s.payMode) this.payMode = s.payMode
        if (s.standardHours) this.standardHours = s.standardHours
        if (s.payOvertimeRate != null) this.payOvertimeRate = s.payOvertimeRate
        if (s.payOvertimeGrace != null) this.payOvertimeGrace = s.payOvertimeGrace
        if (s.payDefaults) this.payDefaults = { monthly: null, daily: null, hourly: null, ...s.payDefaults }
        if (s.payRates) this.payRates = s.payRates
      } catch (e) { /* ignore malformed settings */ }
    },
    openEmployee (row) {
      this.selectedKey = row.key
    },
    closeEmployee () {
      this.selectedKey = null
    },
    onDeviceChange (val) {
      this.deviceSn = val
      this.loadReport()
    },
    loadDevices () {
      axios.get(`${this.$apiAdress}/api/attendance/devices`)
        .then(r => { this.devices = (r.data && r.data.devices) || [] })
        .catch(() => { this.devices = [] })
    },
    loadReport () {
      this.errorMessage = ''
      this.isLoading = true
      this.selectedKey = null
      axios.get(`${this.$apiAdress}/api/attendance/monthly`, {
        params: { month: this.month, device_sn: this.deviceSn || undefined }
      })
        .then(r => {
          const d = r.data || {}
          this.rows = d.employees || []
          this.meta = {
            days_in_month: d.days_in_month || 30,
            office_active_days: d.office_active_days || 0,
            active_dates: d.active_dates || []
          }
        })
        .catch(e => { this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Could not load the monthly attendance report.' })
        .finally(() => { this.isLoading = false })
    },
    exportCsv () {
      const head = ['Employee', 'Code', 'Present Days', 'Working Days', 'Attendance %', 'Full Days', 'One-Punch Days', 'Total Hours', 'Regular Hours', 'Overtime Hours', 'OT Days', 'Avg Hours/Day']
      if (this.payEnabled) head.push('Estimated Pay (INR)')
      const esc = (v) => {
        const s = String(v == null ? '' : v)
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
      }
      const lines = [head.join(',')]
      this.filteredRows.forEach((r) => {
        const row = [r.name, r.employee_code || '', r.present_days, this.officeActiveDays, r.attendancePct,
          r.complete_days, r.incomplete_days, this.formatHours(r.total_hours), this.formatHours(r.regular_hours),
          this.formatHours(r.overtime_hours), r.ot_days, this.formatHours(r.avg_hours)]
        if (this.payEnabled) row.push(r.estimated_pay != null ? r.estimated_pay : '')
        lines.push(row.map(esc).join(','))
      })
      const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `payroll-attendance-${this.month}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.pm-page { padding: 0.25rem 0; }

/* ── Toolbar ── */
.pm-toolbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; padding: 0.25rem 0.25rem 1rem; }
.pm-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.pm-toolbar__accent { width: 4px; height: 40px; border-radius: 4px; background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%); }
.pm-toolbar__title h1 { margin: 0; font-size: 1.15rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.pm-toolbar__title p { margin: 0.12rem 0 0; font-size: 0.78rem; color: #6b7280; max-width: 680px; }
.pm-toolbar__actions { display: flex; align-items: center; gap: 0.5rem; }

.pm-btn { display: inline-flex; align-items: center; gap: 0.45rem; height: 34px; padding: 0 0.85rem; border-radius: 8px; font-size: 0.82rem; font-weight: 600; cursor: pointer; transition: all 0.15s ease; }
.pm-btn--ghost { background: #fff; border: 1px solid #e5e9f2; color: #1f2a44; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04); }
.pm-btn--ghost:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12); }
.pm-btn--ghost:disabled { opacity: 0.55; cursor: not-allowed; }
.pm-btn__spin { color: #16a07a; flex-shrink: 0; }
.pm-btn.is-loading .pm-btn__spin { animation: pm-spin 0.9s linear infinite; }
@keyframes pm-spin { to { transform: rotate(360deg); } }
.pm-btn:focus { outline: none; }
.pm-btn::-moz-focus-inner { border: 0; }
.pm-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.pm-btn--pay { background: #fff; border: 1px solid #e5e9f2; color: #475569; width: 100%; justify-content: center; }
.pm-btn--pay:hover { border-color: #16a07a; color: #0e5b46; }
.pm-btn--pay.is-on { background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%); border-color: transparent; color: #fff; box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28); }

.pm-alert { margin: 0 0 1rem; border-radius: 10px; font-size: 0.85rem; }

/* ── Controls ── */
.pm-section { background: #fff; border: 1px solid #e5e9f2; border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); margin-bottom: 1.25rem; }
.pm-controls { display: flex; flex-wrap: wrap; gap: 0.85rem; align-items: flex-end; padding: 1rem; }
.pm-field { display: flex; flex-direction: column; flex: 1 1 200px; min-width: 0; }
.pm-field--search { flex: 2 1 240px; }
.pm-field--action { flex: 1 1 170px; }
.pm-label { font-size: 0.78rem; font-weight: 600; color: #4f5560; margin-bottom: 0.3rem; }

/* month stepper — one unified segmented control */
.pm-field--month { flex: 1 1 250px; }
.pm-month { display: flex; align-items: stretch; width: 100%; height: 36px; border: 1px solid #d8dde6; border-radius: 8px; background: #fff; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.pm-month:hover { border-color: #c2cad6; }
.pm-month:focus-within { border-color: #16a07a; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18); }
.pm-month__nav { display: inline-flex; align-items: center; justify-content: center; width: 38px; flex-shrink: 0; border: 0; background: transparent; color: #64748b; cursor: pointer; transition: color 0.15s ease, background 0.15s ease; }
.pm-month__nav:first-child { border-radius: 7px 0 0 7px; }
.pm-month__nav:last-child { border-radius: 0 7px 7px 0; }
.pm-month__nav:hover:not(:disabled) { color: #0e5b46; background: #f5fbf8; }
.pm-month__nav:disabled { opacity: 0.32; cursor: not-allowed; }
.pm-month__nav:focus { outline: none; }
.pm-month__nav:focus-visible { outline: none; box-shadow: inset 0 0 0 2px rgba(22, 160, 122, 0.4); }
.pm-month__picker { position: relative; flex: 1 1 auto; min-width: 0; border-left: 1px solid #eef1f7; border-right: 1px solid #eef1f7; }
.pm-month__current { display: flex; align-items: center; justify-content: center; gap: 0.4rem; width: 100%; height: 100%; padding: 0 0.5rem; border: 0; background: transparent; color: #1f2a44; font-size: 0.85rem; font-weight: 700; line-height: 1; white-space: nowrap; cursor: pointer; transition: background 0.15s ease; }
.pm-month__current span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pm-month__current:hover, .pm-month__current.is-open { background: #f5fbf8; }
.pm-month__current > svg:first-child { color: #16a07a; flex-shrink: 0; }
.pm-month__chev { color: #94a3b8; flex-shrink: 0; transition: transform 0.18s ease, color 0.18s ease; }
.pm-month__current.is-open .pm-month__chev { transform: rotate(180deg); color: #16a07a; }
.pm-month__current:focus { outline: none; }
.pm-month__current:focus-visible { outline: none; box-shadow: inset 0 0 0 2px rgba(22, 160, 122, 0.4); }

.pm-monthpop__nav { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; flex-shrink: 0; border: 1px solid #e5e9f2; border-radius: 7px; background: #fff; color: #475569; cursor: pointer; transition: all 0.15s ease; }
.pm-monthpop__nav:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.pm-monthpop__nav:disabled { opacity: 0.4; cursor: not-allowed; }
.pm-monthpop__nav:focus { outline: none; }
.pm-monthpop__nav:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.pm-monthpop { position: absolute; z-index: 30; top: calc(100% + 6px); left: 0; right: 0; min-width: 220px; background: #fff; border: 1px solid #e5e9f2; border-radius: 12px; box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18); padding: 0.75rem; }
.pm-monthpop__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.6rem; }
.pm-monthpop__year { font-size: 0.92rem; font-weight: 700; color: #1f2a44; }
.pm-monthpop__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.35rem; }
.pm-monthpop__cell { height: 34px; border: 1px solid transparent; border-radius: 7px; background: #f8fafc; color: #475569; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: all 0.13s ease; }
.pm-monthpop__cell:hover:not(:disabled) { background: #f0f9f4; color: #0e5b46; }
.pm-monthpop__cell.is-current { border-color: #c7e9da; }
.pm-monthpop__cell.is-active { background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%); color: #fff; box-shadow: 0 4px 10px rgba(14, 91, 70, 0.26); }
.pm-monthpop__cell:disabled { opacity: 0.35; cursor: not-allowed; }
.pm-monthpop__cell:focus { outline: none; }
.pm-monthpop__cell:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.pm-search { position: relative; display: flex; align-items: center; }
.pm-search__icon { position: absolute; left: 0.65rem; color: #94a3b8; pointer-events: none; }
.pm-search__input { width: 100%; height: 36px; padding: 0.3rem 2rem 0.3rem 2.1rem; border: 1px solid #d8dde6; border-radius: 8px; font-size: 0.85rem; color: #2f3640; background: #fff; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.pm-search__input::placeholder { color: #9ca3af; }
.pm-search__input:focus { outline: none; border-color: #16a07a; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18); }
.pm-search__clear { position: absolute; right: 0.45rem; width: 20px; height: 20px; display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: 50%; background: #eef1f7; color: #64748b; font-size: 1rem; line-height: 1; cursor: pointer; }
.pm-search__clear:hover { background: #e2e8f0; color: #334155; }
.pm-search__clear:focus { outline: none; }
.pm-search__clear:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

/* pay basis bar */
.pm-paybar { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 1rem; padding: 0.9rem 1rem; border-top: 1px dashed #e5e9f2; background: #fbfcfe; border-radius: 0 0 12px 12px; }
.pm-paybar__mode { display: inline-flex; gap: 0.3rem; padding: 0.25rem; background: #fff; border: 1px solid #e5e9f2; border-radius: 999px; }
.pm-paychip { padding: 0.35rem 0.85rem; border: 0; border-radius: 999px; background: transparent; color: #64748b; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: all 0.15s ease; }
.pm-paychip:hover { color: #334155; }
.pm-paychip.is-active { background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%); color: #fff; box-shadow: 0 3px 8px rgba(14, 91, 70, 0.24); }
.pm-paychip:focus { outline: none; }
.pm-paychip:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
.pm-paybar__rlabel, .pm-paybar__rate label { font-size: 0.74rem; font-weight: 600; color: #4f5560; margin-bottom: 0.3rem; display: block; }
.pm-paybar__note { flex: 1 1 100%; margin: 0; font-size: 0.74rem; color: #94a3b8; }

.pm-rateinput { display: inline-flex; align-items: center; height: 36px; border: 1px solid #d8dde6; border-radius: 8px; background: #fff; padding: 0 0.5rem; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.pm-rateinput:focus-within { border-color: #16a07a; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18); }
.pm-rateinput--sm { width: 96px; }
.pm-rateinput__cur, .pm-rateinput__suf { color: #94a3b8; font-size: 0.85rem; font-weight: 600; }
.pm-rateinput__field { border: 0; outline: none; background: transparent; width: 120px; height: 100%; padding: 0 0.35rem; font-size: 0.86rem; font-weight: 600; color: #1f2a44; font-variant-numeric: tabular-nums; }
.pm-rateinput--sm .pm-rateinput__field { width: 48px; }
.pm-rateinput__reset { border: 0; background: #eef1f7; color: #64748b; width: 22px; height: 22px; border-radius: 6px; cursor: pointer; font-size: 0.9rem; line-height: 1; }
.pm-rateinput__reset:hover { background: #e2e8f0; color: #0e5b46; }

.pm-collapse-enter-active, .pm-collapse-leave-active { transition: opacity 0.2s ease; }
.pm-collapse-enter, .pm-collapse-leave-to { opacity: 0; }

/* ── KPIs ── */
.pm-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 0.85rem; margin-bottom: 1.25rem; }
.pm-kpi { display: flex; align-items: center; gap: 0.75rem; background: #fff; border: 1px solid #e5e9f2; border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); padding: 0.85rem 1rem; transition: box-shadow 0.18s ease, transform 0.18s ease; }
.pm-kpi:hover { box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08); transform: translateY(-1px); }
.pm-kpi--accent { border-color: #c7e9da; background: linear-gradient(180deg, #f3fbf7 0%, #ffffff 60%); }
.pm-kpi__icon { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 11px; flex-shrink: 0; }
.pm-kpi__icon--present { background: #dcfce7; color: #16834f; }
.pm-kpi__icon--attend { background: #e0f2fe; color: #0369a1; }
.pm-kpi__icon--days { background: #ede9fe; color: #6d28d9; }
.pm-kpi__icon--brand { background: #e6f5ef; color: #0e5b46; }
.pm-kpi__icon--ot { background: #ffedd5; color: #c2410c; }
.pm-kpi__meta { display: flex; flex-direction: column; gap: 0.05rem; min-width: 0; }
.pm-kpi__label { font-size: 0.7rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.pm-kpi__value { font-size: 1.5rem; font-weight: 700; color: #0e5b46; line-height: 1.1; }
.pm-kpi__unit { font-size: 0.8rem; font-weight: 600; color: #16a07a; margin-left: 0.15rem; }
.pm-kpi__sub { font-size: 0.7rem; color: #94a3b8; font-weight: 500; }

/* ── Office rhythm ── */
.pm-rhythm { padding: 1rem 1.1rem 1.15rem; }
.pm-rhythm__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.85rem; }
.pm-rhythm__head h2 { margin: 0; font-size: 0.95rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.pm-rhythm__head small { font-size: 0.76rem; color: #94a3b8; }
.pm-rhythm__legend { display: flex; gap: 0.5rem; flex-shrink: 0; }
.pm-rhythm__peak, .pm-rhythm__total { font-size: 0.72rem; font-weight: 600; padding: 0.2rem 0.55rem; border-radius: 999px; }
.pm-rhythm__peak { background: #e6f5ef; color: #0e5b46; }
.pm-rhythm__total { background: #f1f5f9; color: #475569; }
.pm-rhythm__chart { display: flex; align-items: flex-end; gap: 3px; height: 96px; padding-top: 0.25rem; }
.pm-rbar { flex: 1 1 0; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; min-width: 0; position: relative; }
.pm-rbar__fill { width: 100%; max-width: 16px; border-radius: 4px 4px 2px 2px; background: linear-gradient(180deg, #16a07a 0%, #0e5b46 100%); min-height: 2px; transition: height 0.3s ease, opacity 0.15s ease; }
.pm-rbar.is-weekend .pm-rbar__fill { background: linear-gradient(180deg, #94d3bf 0%, #6cbfa3 100%); }
.pm-rbar.is-empty .pm-rbar__fill { background: #eef1f7; }
.pm-rbar:hover .pm-rbar__fill { opacity: 0.82; }
.pm-rbar__day { font-size: 0.55rem; color: #cbd5e1; margin-top: 3px; font-variant-numeric: tabular-nums; }
.pm-rbar:hover .pm-rbar__day { color: #0e5b46; font-weight: 700; }

.pm-hint { font-size: 0.78rem; color: #6b7280; font-weight: 600; }

/* ── Employee cell ── */
.pm-emp { display: flex; align-items: center; gap: 0.6rem; cursor: pointer; }
.pm-avatar { position: relative; display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0; overflow: hidden; font-size: 0.74rem; font-weight: 700; color: #0e5b46; background: linear-gradient(135deg, #e6f5ef 0%, #d4ede2 100%); }
.pm-avatar--unmapped { color: #b45309; background: #fef3c7; }
.pm-avatar--photo { background: #f1f5f9; box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06); }
.pm-avatar--lg { width: 48px; height: 48px; font-size: 1rem; }
.pm-avatar__img { width: 100%; height: 100%; object-fit: cover; display: block; }
.pm-emp__text { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
.pm-emp__name { font-weight: 600; color: #1f2a44; font-size: 0.86rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pm-emp__code { font-size: 0.72rem; color: #94a3b8; font-variant-numeric: tabular-nums; }
.pm-emp__code--warn { color: #b45309; }
.pm-emp__open { color: #cbd5e1; margin-left: auto; flex-shrink: 0; transition: transform 0.15s ease, color 0.15s ease; }
.pm-emp:hover .pm-emp__open { color: #16a07a; transform: translateX(2px); }

/* attendance ring */
.pm-ring { position: relative; display: inline-flex; align-items: center; justify-content: center; width: 46px; height: 46px; }
.pm-ring__svg { width: 46px; height: 46px; transform: rotate(-90deg); }
.pm-ring__track { fill: none; stroke: #eef1f7; stroke-width: 3.5; }
.pm-ring__fill { fill: none; stroke-width: 3.5; stroke-linecap: round; transition: stroke-dasharray 0.4s ease; }
.pm-ring__fill--full { stroke: #16a07a; }
.pm-ring__fill--mid { stroke: #f59e0b; }
.pm-ring__fill--low { stroke: #ef4444; }
.pm-ring__pct { position: absolute; font-size: 0.72rem; font-weight: 700; color: #1f2a44; font-variant-numeric: tabular-nums; }
.pm-ring__pct small { font-size: 0.55rem; color: #94a3b8; }

/* days cell */
.pm-days { display: flex; flex-direction: column; align-items: center; gap: 0.25rem; }
.pm-days__main { font-size: 0.92rem; font-weight: 700; color: #1f2a44; font-variant-numeric: tabular-nums; }
.pm-days__sep { color: #cbd5e1; margin: 0 1px; }
.pm-days__den { color: #94a3b8; font-weight: 600; font-size: 0.8rem; }
.pm-days__chips { display: inline-flex; gap: 0.2rem; }
.pm-chip { min-width: 18px; padding: 0 0.3rem; height: 17px; display: inline-flex; align-items: center; justify-content: center; border-radius: 5px; font-size: 0.66rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.pm-chip--green { background: #dcfce7; color: #166534; }
.pm-chip--amber { background: #fef3c7; color: #92400e; }
.pm-chip--red { background: #fee2e2; color: #b91c1c; }
.pm-chip--gray { background: #f1f5f9; color: #94a3b8; }

.pm-num { font-size: 0.9rem; font-weight: 700; color: #1f2a44; font-variant-numeric: tabular-nums; }
.pm-num--muted { color: #475569; font-weight: 600; }
.pm-num__u { font-size: 0.7rem; color: #94a3b8; margin-left: 1px; }
.pm-muted { color: #cbd5e1; }
.pm-pay { font-size: 0.9rem; font-weight: 700; color: #0e5b46; font-variant-numeric: tabular-nums; }

.pm-tot { display: inline-flex; flex-direction: column; align-items: flex-end; gap: 1px; }
.pm-tot__reg { font-size: 0.64rem; color: #94a3b8; font-weight: 600; font-variant-numeric: tabular-nums; }
.pm-ot { font-size: 0.9rem; font-weight: 700; color: #c2410c; font-variant-numeric: tabular-nums; }
.pm-ot__u { font-size: 0.7rem; color: #ea9a6a; margin-left: 1px; }

/* sparkline */
.pm-spark { display: flex; align-items: flex-end; gap: 1px; height: 30px; width: 100%; }
.pm-spark__bar { flex: 1 1 0; min-width: 0; border-radius: 1.5px; min-height: 2px; }
.pm-spark__bar--green { background: #16a07a; }
.pm-spark__bar--ot { background: #0e5b46; box-shadow: inset 0 2px 0 #f59e0b; }
.pm-spark__bar--amber { background: #f59e0b; }
.pm-spark__bar--none { background: #eef1f7; }

/* status pill */
.pm-pill { display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.15rem 0.5rem; border-radius: 999px; font-size: 0.68rem; font-weight: 600; white-space: nowrap; }
.pm-pill__dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }
.pm-pill--present { background: #dcfce7; color: #166534; }
.pm-pill--absent { background: #fee2e2; color: #991b1b; }
.pm-pill--incomplete { background: #fef3c7; color: #92400e; }

/* ── Drawer ── */
.pm-drawer { position: fixed; inset: 0; z-index: 1050; }
.pm-drawer__scrim { position: absolute; inset: 0; background: rgba(15, 23, 42, 0.42); backdrop-filter: blur(2px); }
.pm-drawer__panel { position: absolute; top: 0; right: 0; bottom: 0; width: min(560px, 100%); background: #f7f9fc; box-shadow: -16px 0 48px rgba(15, 23, 42, 0.24); display: flex; flex-direction: column; }
.pm-drawer__head { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 1.1rem 1.25rem; background: #fff; border-bottom: 1px solid #eef1f7; }
.pm-drawer__id { display: flex; align-items: center; gap: 0.75rem; min-width: 0; }
.pm-drawer__id h3 { margin: 0; font-size: 1.02rem; font-weight: 700; color: #1f2a44; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pm-drawer__id p { margin: 0.1rem 0 0; font-size: 0.76rem; color: #6b7280; }
.pm-drawer__close { width: 34px; height: 34px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; border: 1px solid #e5e9f2; border-radius: 8px; background: #fff; color: #64748b; cursor: pointer; transition: all 0.15s ease; }
.pm-drawer__close:hover { border-color: #ef4444; color: #b91c1c; background: #fef2f2; }
.pm-drawer__close:focus { outline: none; }
.pm-drawer__close:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
.pm-drawer__body { flex: 1; overflow-y: auto; padding: 1.1rem 1.25rem; display: flex; flex-direction: column; gap: 1rem; }

.pm-statgrid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.6rem; }
.pm-stat { background: #fff; border: 1px solid #e5e9f2; border-radius: 10px; padding: 0.65rem 0.75rem; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03); }
.pm-stat__label { display: block; font-size: 0.66rem; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.2rem; }
.pm-stat__value { font-size: 1.2rem; font-weight: 700; color: #0e5b46; line-height: 1; font-variant-numeric: tabular-nums; }
.pm-stat__value small { font-size: 0.72rem; color: #94a3b8; font-weight: 600; margin-left: 2px; }
.pm-stat__value--sm { font-size: 0.86rem; }
.pm-stat--ot { border-color: #fed7aa; background: linear-gradient(180deg, #fff7ed 0%, #ffffff 70%); }
.pm-stat--ot .pm-stat__value { color: #c2410c; }
.pm-stat__foot { display: block; font-size: 0.62rem; color: #ea9a6a; font-weight: 600; margin-top: 0.15rem; }

/* calendar */
.pm-cal { background: #fff; border: 1px solid #e5e9f2; border-radius: 12px; padding: 0.9rem 1rem 1rem; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03); }
.pm-cal__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.6rem; }
.pm-cal__head h4, .pm-paycard__head h4, .pm-daylist h4 { margin: 0; font-size: 0.85rem; font-weight: 700; color: #1f2a44; }
.pm-cal__legend { display: flex; align-items: center; gap: 0.25rem; }
.pm-cal__lg { font-size: 0.66rem; color: #94a3b8; }
.pm-cal__swatch { width: 12px; height: 12px; border-radius: 3px; }
.pm-cal__dow { display: grid; grid-template-columns: repeat(7, 1fr); gap: 5px; margin-bottom: 5px; }
.pm-cal__dow span { text-align: center; font-size: 0.64rem; font-weight: 700; color: #cbd5e1; text-transform: uppercase; }
.pm-cal__grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 5px; }
.pm-cal__cell { aspect-ratio: 1 / 1; border-radius: 7px; background: #f1f5f9; display: flex; align-items: flex-start; justify-content: flex-end; padding: 3px 5px; position: relative; }
.pm-cal__cell.is-blank { background: transparent; }
.pm-cal__cell.is-empty { background: #f5f7fa; }
.pm-cal__cell.is-absent { background: #fee2e2; }
.pm-cal__cell.is-incomplete { box-shadow: inset 0 0 0 1.5px #fbbf24; }
.pm-cal__num { font-size: 0.66rem; font-weight: 700; opacity: 0.85; }
.pm-cal__cell.is-empty .pm-cal__num { color: #cbd5e1; }
.pm-cal__cell.is-absent .pm-cal__num { color: #dc2626; }
.pm-cal__ot { position: absolute; left: 4px; bottom: 3px; font-size: 0.5rem; font-weight: 800; letter-spacing: 0.03em; line-height: 1; opacity: 0.92; }

/* pay card */
.pm-paycard { background: #fff; border: 1px solid #c7e9da; border-radius: 12px; padding: 0.9rem 1rem; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03); }
.pm-paycard__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.7rem; }
.pm-paycard__mode { font-size: 0.68rem; font-weight: 700; color: #0e5b46; background: #e6f5ef; padding: 0.15rem 0.55rem; border-radius: 999px; }
.pm-paycard__row { display: flex; align-items: flex-end; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.pm-paycard__def { color: #94a3b8; font-weight: 500; }
.pm-paycard__result { text-align: right; }
.pm-paycard__rlabel { display: block; font-size: 0.68rem; color: #94a3b8; font-weight: 600; margin-bottom: 0.1rem; }
.pm-paycard__amount { font-size: 1.35rem; font-weight: 700; color: #0e5b46; font-variant-numeric: tabular-nums; }

/* day list */
.pm-daylist { background: #fff; border: 1px solid #e5e9f2; border-radius: 12px; padding: 0.9rem 1rem 0.5rem; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03); }
.pm-daylist h4 { margin-bottom: 0.5rem; }
.pm-daylist__count { font-size: 0.66rem; font-weight: 700; color: #b91c1c; background: #fee2e2; padding: 0.1rem 0.45rem; border-radius: 999px; margin-left: 0.4rem; }
.pm-daylist__empty { font-size: 0.82rem; color: #94a3b8; padding: 0.5rem 0 1rem; }
.pm-daytable tbody tr.is-absent td { color: #b91c1c; background: #fef5f5; }
.pm-daytable tbody tr.is-absent:hover td { background: #fdeaea; }
.pm-daytable { width: 100%; border-collapse: collapse; font-size: 0.78rem; }
.pm-daytable thead th { text-align: left; font-size: 0.64rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; padding: 0.4rem 0.5rem; border-bottom: 1px solid #eef1f7; }
.pm-daytable tbody td { padding: 0.45rem 0.5rem; border-bottom: 1px solid #f5f7fa; color: #334155; }
.pm-daytable tbody tr:last-child td { border-bottom: none; }
.pm-daytable tbody tr:hover td { background: #f5fbf8; }
.pm-r { text-align: right; }
.pm-mono { font-variant-numeric: tabular-nums; }
.pm-ot-tag { display: inline-block; padding: 0.05rem 0.35rem; border-radius: 5px; background: #ffedd5; color: #c2410c; font-size: 0.7rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.pm-paybar__hint { font-weight: 500; color: #94a3b8; font-size: 0.66rem; }

.pm-drawer-enter-active, .pm-drawer-leave-active { transition: opacity 0.22s ease; }
.pm-drawer-enter-active .pm-drawer__panel, .pm-drawer-leave-active .pm-drawer__panel { transition: transform 0.26s cubic-bezier(0.22, 1, 0.36, 1); }
.pm-drawer-enter .pm-drawer__panel, .pm-drawer-leave-to .pm-drawer__panel { transform: translateX(100%); }
.pm-drawer-enter, .pm-drawer-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .pm-statgrid { grid-template-columns: repeat(2, 1fr); }
  .pm-emp__name { max-width: 140px; }
}
</style>
